/**
 * A gift line's recipient, shared by Record a payment and the Mail Order Batch:
 * the state, the duplicate gate (the same `dupGate` both screens use) and the
 * body each line sends. The picker itself is RecipientPicker.tsx.
 */
import { useQueries } from '@tanstack/react-query'
import { useDebounced } from '../lib/useDebounced'
import { matchPeople, type MatchCandidate, type NewPersonBody } from './api'
import { countryValue, dupGate, personKey, type CountryChoice } from './payment'

export interface Address {
  company: string; address_1: string; address_2: string; city: string; state: string; zipcode: string; email: string
  country: CountryChoice; country_other: string
}
export const BLANK_ADDRESS: Address = {
  company: '', address_1: '', address_2: '', city: '', state: '', zipcode: '', email: '', country: 'US', country_other: '',
}

export interface Recipient {
  mode: 'search' | 'existing'
  name: string
  id?: number
  label?: string
  dismissedKey: string | null
  address: Address
}
export const blankRcpt = (): Recipient => ({ mode: 'search', name: '', dismissedKey: null, address: { ...BLANK_ADDRESS } })

export function splitName(full: string): { first_name: string; last_name: string } {
  const parts = full.trim().split(/\s+/).filter(Boolean)
  if (parts.length <= 1) return { first_name: '', last_name: parts[0] ?? '' }
  return { first_name: parts.slice(0, -1).join(' '), last_name: parts[parts.length - 1] }
}

export const nz = (s: string): string | null => (s.trim() ? s.trim() : null)

export const personBody = (name: { first_name: string; last_name: string }, a: Address, extra?: { phone?: string }): NewPersonBody => ({
  first_name: nz(name.first_name), last_name: nz(name.last_name), company: nz(a.company),
  address_1: nz(a.address_1), address_2: nz(a.address_2), city: nz(a.city),
  state: nz(a.state), zipcode: nz(a.zipcode), email: nz(a.email), phone: nz(extra?.phone ?? ''),
  country: countryValue(a.country, a.country_other),
})

export interface RecipientGate { found: MatchCandidate[]; asking: boolean; blocked: boolean; searching: boolean }

/** A recipient the line can be saved with: someone on file, or a new person
 *  with a name and street whom the duplicate check has let through. */
export function recipientReady(r: Recipient, gate: Pick<RecipientGate, 'blocked'>): boolean {
  if (r.mode === 'existing') return r.id !== undefined
  return !gate.blocked && r.name.trim().length >= 3 && r.address.address_1.trim() !== ''
    && (r.address.country !== 'OTHER' || r.address.country_other.trim() !== '')
}

/** What a gift line sends for its recipient. */
export function recipientLineFields(r: Recipient): { recipient_subscriber_id: number } | { new_recipient: NewPersonBody } {
  if (r.mode === 'existing' && r.id !== undefined) return { recipient_subscriber_id: r.id }
  return { new_recipient: personBody(splitName(r.name), r.address) }
}

/** Identifies the recipient as typed: the same key means the same person on a retry. */
export const recipientKey = (r: Recipient | undefined): string =>
  !r ? '' : r.mode === 'existing' ? `#${r.id ?? ''}` : personKey(r.name, r.address.address_1, r.address.zipcode, r.address.email)

/** The parts of a new recipient a "these are all different people" answer is given for. */
export const newPersonParts = (r: Recipient): string[] =>
  r.mode === 'search' ? [r.name, r.address.address_1, r.address.zipcode, r.address.email] : []

/**
 * The duplicate gate for each gift line (null for a line that is not a gift).
 * The typed name is searched once it settles; people found other than
 * `excludeId` (the payer) are offered before a new person can be saved.
 */
export function useRecipientGates(rcpts: (Recipient | null)[], excludeId: number | null): RecipientGate[] {
  const debouncedNames = useDebounced(rcpts.map((r) => r?.name ?? ''), 350)
  const search = rcpts.map((r, i) => ({
    searching: !!r && r.mode === 'search' && r.name.trim().length >= 3,
    settledName: personKey(debouncedNames[i] ?? '') === personKey(r?.name ?? ''),
  }))
  const queries = useQueries({
    queries: rcpts.map((_r, i) => {
      const parts = splitName(debouncedNames[i] ?? '')
      return {
        queryKey: ['desk-match', parts.first_name, parts.last_name],
        queryFn: () => matchPeople(parts),
        enabled: search[i].searching && search[i].settledName,
      }
    }),
  })
  return rcpts.map((r, i) => {
    const q = queries[i]
    const found = (q.data?.candidates ?? []).filter((c) => c.subscriber_id !== excludeId)
    const gate = dupGate({
      searching: search[i].searching,
      settled: search[i].settledName && q.data !== undefined && !q.isFetching,
      candidates: found.length, dismissedKey: r?.dismissedKey ?? null, key: personKey(r?.name ?? ''),
    })
    return { found, ...gate, searching: search[i].searching }
  })
}
